// Renderização segura do texto de comentários (blog/colunas).
//
// Os comentários chegam do backend como TEXTO CRU digitado pelo usuário (ou pelo
// atendimento), podendo conter tags — normalmente um <a href="...">link</a>.
// Interpolar com {{ }} escapa tudo e a tag aparece literal na tela; usar v-html
// direto abriria XSS (o conteúdo é enviado por terceiros).
//
// A estratégia aqui é o meio-termo: TUDO é escapado, e só então reconstruímos
// os links — a partir de âncoras <a href> presentes no texto e de URLs soltas.
// Qualquer outra tag continua aparecendo como texto (escapada), sem executar.

// Âncora completa no texto cru: captura os atributos e o rótulo interno.
const ANCHOR_RE = /<a\b([^>]*)>([\s\S]*?)<\/a\s*>/gi;

// href dentro dos atributos: aceita aspas retas, simples ou valor sem aspas
// (é este último caso que pega o `href=”https://...` com aspa tipográfica,
// limpo depois em normalizeHref).
const HREF_RE = /href\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;

// URL solta no meio do texto (sem tag), incluindo o formato "www.".
const BARE_URL_RE = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

// Aspas (retas e tipográficas) que podem sobrar nas pontas de um href malformado.
const QUOTES_RE = /^[\s"'“”‘’]+|[\s"'“”‘’]+$/g;

const HOST_INTERNO = "belladasemana.com.br";

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Desfaz as entidades que o backend/editor possa ter gravado no href antes de
// validá-lo (ex.: `?a=1&amp;b=2`). O valor é reescapado ao montar o <a>.
function decodeEntities(text: string): string {
  return text
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">");
}

// Devolve a URL absoluta http(s) ou null (qualquer outro esquema — javascript:,
// data:, etc. — é recusado e o trecho vira texto simples).
function normalizeHref(raw: string): string | null {
  const cleaned = decodeEntities(raw).replace(QUOTES_RE, "");
  if (!cleaned) return null;
  // Caminho interno do próprio site ("/pt-br/modelos/..."). "//host" e "/\host"
  // são recusados: o navegador resolve os dois como host EXTERNO, então o link
  // pareceria interno e levaria para fora (isca de phishing).
  if (cleaned.startsWith("/")) {
    return /^\/[\\/]/.test(cleaned) ? null : cleaned;
  }
  const candidate = /^www\./i.test(cleaned) ? `https://${cleaned}` : cleaned;
  if (!/^https?:\/\//i.test(candidate)) return null;
  try {
    return new URL(candidate).href;
  } catch {
    return null;
  }
}

// Links do próprio site abrem na mesma aba; externos em nova aba.
function isExternal(href: string): boolean {
  if (href.startsWith("/")) return false;
  try {
    const { hostname } = new URL(href);
    return hostname !== HOST_INTERNO && !hostname.endsWith(`.${HOST_INTERNO}`);
  } catch {
    return true;
  }
}

function buildAnchor(href: string, label: string): string {
  const target = isExternal(href)
    ? ' target="_blank" rel="noopener noreferrer nofollow"'
    : "";
  return `<a href="${escapeHtml(href)}"${target}>${escapeHtml(label)}</a>`;
}

// Pontuação final que costuma ser da frase, não da URL ("acesse o site.").
function trimTrailingPunctuation(url: string): { url: string; rest: string } {
  const match = url.match(/[.,;:!?)\]]+$/);
  if (!match) return { url, rest: "" };
  return { url: url.slice(0, -match[0].length), rest: match[0] };
}

// Escapa um trecho sem tags e transforma as URLs soltas em links.
function linkifyPlain(text: string): string {
  if (!text) return "";
  let out = "";
  let last = 0;
  BARE_URL_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = BARE_URL_RE.exec(text)) !== null) {
    const { url, rest } = trimTrailingPunctuation(match[0]);
    const href = normalizeHref(url);
    out += escapeHtml(text.slice(last, match.index));
    out += href ? buildAnchor(href, url) : escapeHtml(url);
    out += escapeHtml(rest);
    last = match.index + match[0].length;
  }
  return out + escapeHtml(text.slice(last));
}

// Tags de âncora SOLTAS (abertura sem fechamento, ou </a> sobrando) — comuns em
// comentário digitado à mão. A abertura vira um link rotulado pela própria URL;
// o fechamento órfão é descartado. Sem isso o trecho apareceria como tag exposta.
const ORPHAN_ANCHOR_RE = /<a\b([^>]*)>|<\/a\s*>/gi;

function renderPlainSegment(text: string): string {
  if (!text) return "";
  let out = "";
  let last = 0;
  ORPHAN_ANCHOR_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = ORPHAN_ANCHOR_RE.exec(text)) !== null) {
    out += linkifyPlain(text.slice(last, match.index));
    if (match[1] !== undefined) {
      const hrefMatch = match[1].match(HREF_RE);
      const href = normalizeHref(
        hrefMatch?.[1] ?? hrefMatch?.[2] ?? hrefMatch?.[3] ?? ""
      );
      if (href) out += buildAnchor(href, href);
    }
    last = match.index + match[0].length;
  }
  return out + linkifyPlain(text.slice(last));
}

// Reconstrói um <a> do texto cru. Sem href válido, mostra só o rótulo (o link
// quebrado não vira tag exposta nem link clicável para lugar nenhum).
function rebuildAnchor(attrs: string, inner: string): string {
  const hrefMatch = attrs.match(HREF_RE);
  const href = normalizeHref(hrefMatch?.[1] ?? hrefMatch?.[2] ?? hrefMatch?.[3] ?? "");
  // O rótulo pode ter tags aninhadas (<b>, <span>): usamos só o texto.
  const label = inner.replace(/<[^>]*>/g, "").trim();
  if (!href) return linkifyPlain(label);
  return buildAnchor(href, label || href);
}

/**
 * Converte o texto de um comentário em HTML seguro para uso com v-html:
 * links clicáveis (de tags <a> ou de URLs digitadas) e todo o resto escapado.
 */
export function renderCommentHtml(raw: unknown): string {
  const text = typeof raw === "string" ? raw : raw == null ? "" : String(raw);
  if (!text) return "";

  let out = "";
  let last = 0;
  ANCHOR_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = ANCHOR_RE.exec(text)) !== null) {
    out += renderPlainSegment(text.slice(last, match.index));
    out += rebuildAnchor(match[1], match[2]);
    last = match.index + match[0].length;
  }
  return out + renderPlainSegment(text.slice(last));
}
